import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { dictionary, LANGS, LANG_LABELS } from "@/lib/i18n";
import { Menu, X } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_cads-solutions/artifacts/fb4d6e1e_photo_5316519551212133005_y.jpg";

const LanguageSwitcher = ({ className = "", testidPrefix = "" }) => {
  const { lang, setLang } = useI18n();
  return (
    <div
      role="group"
      aria-label="Language switcher"
      className={`inline-flex items-stretch border border-[#0F2756]/20 bg-white ${className}`}
    >
      {LANGS.map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            data-testid={`${testidPrefix}lang-${code}`}
            onClick={() => setLang(code)}
            aria-pressed={active}
            className={`px-2.5 py-1.5 text-[11px] font-bold tracking-[0.15em] transition-colors duration-150 border-l first:border-l-0 border-[#0F2756]/10 ${
              active
                ? "bg-[#0F2756] text-white"
                : "text-[#0F2756] hover:bg-[#F5F8FC]"
            }`}
          >
            {LANG_LABELS[code]}
          </button>
        );
      })}
    </div>
  );
};

export const Navbar = () => {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = dictionary.nav[lang];
  const ctaLabel = dictionary.nav.cta[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (id) => (event) => {
    event.preventDefault();
    setOpen(false);
    const node = document.getElementById(id);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-md bg-white/90 border-b border-[#E2E8F0] shadow-[0_1px_0_rgba(15,39,86,0.04)]"
          : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="container mx-auto px-6 md:px-10 max-w-7xl flex items-center justify-between py-2.5">
        <a
          href="#about"
          data-testid="navbar-logo"
          onClick={handleAnchor("about")}
          className="flex items-center gap-2 group shrink-0"
          aria-label="CADS — Central Asia Digital Solutions"
        >
          <img
            src={LOGO_URL}
            alt="CADS — Central Asia Digital Solutions"
            className="h-20 md:h-24 w-auto object-contain"
          />
        </a>

        <nav className="hidden xl:flex items-center gap-7">
          {links.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={handleAnchor(link.id)}
              data-testid={`nav-link-${link.id}`}
              className="text-[13px] font-medium text-[#475569] hover:text-[#0F2756] tracking-wide transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher
            className="hidden sm:inline-flex"
            testidPrefix="navbar-"
          />

          <Button
            data-testid="navbar-cta"
            onClick={handleAnchor("form")}
            className="hidden md:inline-flex bg-[#0F2756] hover:bg-[#1857D6] text-white rounded-none px-5 py-5 font-semibold tracking-wide"
          >
            {ctaLabel}
          </Button>

          <button
            data-testid="navbar-mobile-toggle"
            className="xl:hidden p-2 text-[#0F2756]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="xl:hidden border-t border-[#E2E8F0] bg-white/98 backdrop-blur"
        >
          <div className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={handleAnchor(link.id)}
                data-testid={`nav-mobile-link-${link.id}`}
                className="text-sm text-[#0F2756] hover:text-[#1857D6] tracking-wide"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#E2E8F0]">
              <LanguageSwitcher testidPrefix="navbar-mobile-" />
              <Button
                data-testid="navbar-mobile-cta"
                onClick={handleAnchor("form")}
                className="bg-[#0F2756] hover:bg-[#1857D6] text-white rounded-none px-5 font-semibold tracking-wide"
              >
                {ctaLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
